import { CircularProgress } from "@mui/material";
import axios from "axios";
import React, {
    createContext,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";
import { io } from "socket.io-client";
import { isRoleAdmin } from "./frappeConfig";
import CompanyProfileSelection from "./pages/CompanyProfileSelection/CompanyProfileSelection";
import { useContacts, useCustomers } from "./shared/hooks/useCustomer";
import {
    useGetDocTypeList,
    useGetDocTypeListWithChildren,
} from "./shared/hooks/useDocTypesData";
import {
    useFrappeToken,
    useGetUserSettings,
    useSaveUserSettings,
    useLoadDoctype,
    useSiteName,
} from "./shared/hooks/useFrappeDoctype";
import { useAddons, useItems } from "./shared/hooks/useItemsData";
import { useListDocTypes } from "./shared/hooks/useListDocTypesData";
import { useGetLoggedUser } from "./shared/hooks/useUser";

// Create the context
export const AppContext = createContext();

// Create a provider component
export const AppProvider = ({ children }) => {
    const [globalState, setGlobalState] = useState({
        defaultCurrency: null,
        permissions: null,
        branchName: null,
        location: null,
        loggedInUser: null,
        paymentAccounts: null,
    });

    const { data: frappeToken } = useFrappeToken(true);

    useEffect(() => {
        if (frappeToken) {
            axios.defaults.headers.common[
                "x-frappe-csrf-token"
            ] = `${frappeToken}`;
        }
    }, [frappeToken]);

    const [selectedFloor, setSelectedFloor] = useState(null);
    const [selectedRoom, setSelectedRoom] = useState(null);
    const [floorRooms, setFloorRooms] = useState([]);
    const [tablesMoving, setTablesMoving] = useState(false);

    const [multipleCompanies, setMultipleCompanies] = useState(false);

    const [showClaimTypes, setShowClaimTypes] = useState(false);
    const [isTabsPos, setIsTabsPos] = useState(false);

    const [isAdmin, setAdmin] = useState(false);

    const [userInfo, setUserInfo] = useState({});

    const { data: userInfoData } = useGetLoggedUser(true);

    const isKitchenUser = useMemo(() => {
        const role =
            userInfoData?.role_profile_name || userInfo?.role_profile_name;
        return role === "Kitchen";
    }, [userInfoData, userInfo]);

    const { data: menuAddons } = useAddons();

    const [company, setCompany] = useState({});

    const { data: profileContext } = useLoadDoctype(
        "Profile Context",
        "Profile Context"
    );
    const { data: sitename } = useSiteName();

    const [socket, setSocket] = useState(null);

    useEffect(() => {
        if (sitename) {
            const socket = io(
                window.location.origin.replace("https://", "wss://") +
                // window.location.origin
                    // .replace("https://", "wss://")
                    // .replace("3001", "9000") + `/${sitename}`,
                {
                    transports: ["websocket"],
                }
            );
            setSocket(socket);

            socket.on("connect", () => {
                console.log("connected to socket");
            });
        }
    }, [sitename]);

    useEffect(() => {
        if (profileContext) {
            const _multipleCompanies =
                profileContext.docs?.[0]?.profile_doctype == "Company";
            setMultipleCompanies(_multipleCompanies);
        }
    }, [profileContext]);

    const { data: getAddresses } = useGetDocTypeList({
        doctype: "Address",
    });

    const { data: getPosProfileData, isLoading: isLoadingPosProfile } =
        useGetDocTypeListWithChildren({
            doctype:
                company && !isKitchenUser && userInfo.name
                    ? "POS Profile"
                    : null,
            filters: company ? [["company", "=", company.name]] : null,
        });

    const companyFilter = useMemo(() => {
        if (multipleCompanies) {
            return { company: company.name };
        }
        return {};
    }, [multipleCompanies, company]);

    const { data: floors } = useListDocTypes(
        "getRestaurantFloors",
        "Restaurant Object",
        "",
        { type: "Floor", ...companyFilter },
        ["*"]
    );

    const { data: companies } = useListDocTypes(
        "getCompanies",
        isKitchenUser ? null : "Company",
        "",
        {},
        ["*"]
    );

    const { data: contacts } = useContacts();
    const { data: customers } = useCustomers();
    const [selectedCustomer, setSelectedCustomer] = useState(null);

    const [posProfile, setPosProfile] = useState(null);
    const [discountAmountAllowed, setDiscountAmountAllowed] = useState(false);

    useEffect(() => {
        if (posProfile) {
            setDiscountAmountAllowed(posProfile.allow_discount_change == 1);
        }
    }, [posProfile]);

    const [userMenuSettings, setUserMenuSettings] = useState(null);

    const { data: userSettings } = useGetUserSettings({
        doctype: "menu_settings",
    });

    const { data: companyUserSettings } = useGetUserSettings({
        doctype: "Company",
    });

    const saveCompanyUserSettings = useSaveUserSettings();

    useEffect(() => {
        if (companies) {
            let userCompany = companies[0];
            if (companyUserSettings && companies.length > 1) {
                userCompany =
                    companies.find(
                        (company) =>
                            company.name === companyUserSettings.selectedProfile
                    ) || userCompany;
            }
            //  save to user settings
            saveCompanyUserSettings.mutate({
                doctype: "Company",
                user_settings: {
                    ...(companyUserSettings || {}),
                    selectedProfile: userCompany.name,
                },
            });
            setCompany(userCompany);
        }
    }, [companyUserSettings, companies]);

    const { data: settingsDetails } = useLoadDoctype(
        "Restaurant Settings",
        "Restaurant Settings"
    );

    const foodLateTimeEnabled = useMemo(() => {
        if (settingsDetails) {
            return settingsDetails?.docs[0]?.order_late_time > 0;
        }
        return false;
    }, [settingsDetails]);

    const foodLateTime = useMemo(() => {
        if (foodLateTimeEnabled) {
            return settingsDetails?.docs[0]?.order_late_time;
        }
        return 0;
    }, [foodLateTimeEnabled, settingsDetails]);

    const elevatedPrivileges =
        settingsDetails?.docs[0]?.allow_elevated_privileges == 1;

    const [serviceFee, setServiceFee] = useState(0);

    // useEffect(() => {
    //     if (getCompanyData) {
    //         setCompany(getCompanyData[0]);
    //     }
    // }, [getCompanyData]);

    useEffect(() => {
        if (userSettings) {
            setUserMenuSettings(userSettings);
        }
    }, [userSettings]);

    useEffect(() => {
        if (settingsDetails) {
            const settings = settingsDetails.docs[0];
            const { detailed_daily_expenses, is_tabs_pos, service_charge } =
                settings;

            if (service_charge && service_charge > 0) {
                setServiceFee(service_charge);
            }

            if (detailed_daily_expenses) {
                setShowClaimTypes(true);
            } else {
                setShowClaimTypes(false);
            }
            if (is_tabs_pos == 1) {
                setIsTabsPos(true);
            } else {
                setIsTabsPos(false);
            }
        }
    }, [settingsDetails]);

    useEffect(() => {
        if (getPosProfileData) {
            setPosProfile(getPosProfileData[0]);
        }
    }, [getPosProfileData]);

    useEffect(() => {
        if (userInfoData) {
            setUserInfo(userInfoData);
        }
    }, [userInfoData]);

    useEffect(() => {
        if (
            userInfo.role_profile_name &&
            isRoleAdmin(userInfo.role_profile_name || userInfo.full_name)
        ) {
            setAdmin(true);
        }
    }, [userInfo]);

    useMemo(() => {
        if (floors && floors.length > 0) {
            setSelectedFloor(floors[0]);
        }
    }, [floors]);

    const [loadingTable, setLoadingTable] = useState("");

    const refetchFloorRooms = (table) => {
        if (table || selectedTable) {
            setLoadingTable(table?.name || selectedTable?.name); //TODO FOR LATER
        }
    };

    const [selectedTableName, setSelectedTableName] = useState();
    const [selectedTable, setSelectedTable] = useState({});

    const setTable = (tableName) => {
        setSelectedTableName(tableName);
    };

    const posProfileItemGroups = useMemo(() => {
        return (
            posProfile?.item_groups
                ?.map((item) => item.item_group)
                ?.filter(Boolean) || []
        );
    }, [posProfile]);

    // Scenarios:
    // 1) multipleCompanies=false => treat item_groups as whitelist, but root stays "All Item Groups"
    // 2) multipleCompanies=true + no item_groups => no filtering, root is "All Item Groups"
    // 3) multipleCompanies=true + many item_groups => whitelist filtering, root is "All Item Groups"
    // 4) multipleCompanies=true + exactly one item_group => that group becomes the root for this company's menu
    const menuAllowedGroups = useMemo(() => {
        return posProfileItemGroups.length > 0 ? posProfileItemGroups : null;
    }, [posProfileItemGroups]);

    const menuRootGroupId = useMemo(() => {
        return multipleCompanies && posProfileItemGroups.length === 1
            ? posProfileItemGroups[0]
            : "All Item Groups";
    }, [multipleCompanies, posProfileItemGroups]);

    const { data: menuItems, refetch: refetchMenuItems } = useItems({
        allowedGroups: menuAllowedGroups,
    });

    const getItem = useCallback(
        (item) => {
            if (menuAddons && menuItems) {
                return [...menuItems, menuAddons].find(
                    (i) => i.item_code === item
                );
            }
            return null;
        },
        [menuItems, menuAddons]
    );

    const getMenuItem = (item) => {
        if (menuItems) {
            return menuItems.find((i) => i.item_code === item);
        } else {
            return null;
        }
    };

    // Determine what to render based on company state
    const renderContent = () => {
        // Kitchen users don't require company context to use KDS screens
        if (isKitchenUser) {
            return children;
        }

        // If we're still loading companies or company user settings, show loading
        if (!companies || !userInfo || isLoadingPosProfile) {
            return <CircularProgress />;
        }

        // If there's only one company, auto-select it
        if (companies.length === 1 && !company) {
            return <CircularProgress />;
        }

        // If there are multiple companies and no company is selected (or the selection is invalid), show company selection
        if (companies.length > 1 && !company.name) {
            return <CompanyProfileSelection />;
        }

        // If we have a company selected, render the children
        if (company) {
            return children;
        }

        // Default loading state
        return <CircularProgress />;
    };

    return (
        <AppContext.Provider
            value={{
                globalState,
                setGlobalState,
                floors,
                floorContext: [selectedFloor, setSelectedFloor],
                floorRoomsContext: [floorRooms, setFloorRooms],
                roomContext: [selectedRoom, setSelectedRoom],
                tableContext: [selectedTable, setSelectedTable],
                customerContext: [selectedCustomer, setSelectedCustomer],
                refetchFloorRooms,
                setTable,
                foodLateTime,
                foodLateTimeEnabled,
                tablesMovingContext: [tablesMoving, setTablesMoving],
                menuItems,
                getMenuItem,
                getItem,
                contacts: contacts,
                customers,
                loadingTable,
                setLoadingTable,
                isKitchenUser,
                menuAddons,
                userInfo,
                company,
                posProfile,
                menuAllowedGroups,
                menuRootGroupId,
                userMenuSettings,
                setUserMenuSettings,
                showClaimTypes,
                isTabsPos,
                addresses: getAddresses || [],
                companies, // Add companies to context for CompanyProfileSelection
                multipleCompanies,
                settingsDetails,
                restaurantSettings: settingsDetails,
                serviceFee,
                refetchMenuItems,
                socket,
                elevatedPrivileges,
                discountAmountAllowed,
            }}
        >
            {renderContent()}
        </AppContext.Provider>
    );
};
